import { Autocomplete, Box, Button, TextField, Typography } from '@mui/material'
import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { uniqBy } from 'lodash'
import { BackButton } from '~/src/components/atoms/Button/BackButton/BackButton'
import { CardWithHeaderAndActionButton } from '~/src/components/organisms/Card/CardWithActionButton/CardWithHeaderAndActionButton'
import { OrderStatementOfAccountContent } from '~/src/components/organisms/StatementOfAccount/StatementOfAccountDetail/OrderStatementOfAccountContent'
import { FileUploader } from '~/src/components/organisms/FileUploader/FileUploader'
import { orderRepository } from '~/src/repositories'
import { IGetFileUpload } from '~/src/repositories/Invoice/models/Invoice'
import {
  IPostStatementRequest,
  IStatementDetailParams,
} from '~/src/repositories/Order/models/Order'
import { useMessage } from '~/src/hooks'
import { useUsers } from '~/src/hooks/useApi'
import {
  BoxStyle,
  ColumnBoxStyle,
  TextStyle,
  TextStyle2,
  TextStyle3,
} from './OrderStatementOfAccountDetailForm.styles'

interface IOrderStatementOfAccountDetailForm {
  id?: number
  user?: IStatementDetailParams
}

type TOptions = { userId: number; name: string; email: string }

/**
 * OrderStatementOfAccountDetailForm
 * @param id
 * @param user
 * @constructor
 */
export const OrderStatementOfAccountDetailForm: FC<IOrderStatementOfAccountDetailForm> = ({
  id,
  user,
}): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { handleOpenWithErrorMessage, handleOpenWithSuccessMessage } = useMessage()

  // file up 関連
  const [file, setFile] = useState<File>()
  const [listFile, setListFile] = useState<IGetFileUpload['data']>()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [isDelete, setIsDeleteFile] = useState<boolean>(false)

  // user 関連
  const [selectedUserName, setSelectedName] = useState<string>(user ? user.name : '')
  const [selectedUserIdName, setSelectedUserIdName] = useState<number | undefined>(user?.userId)
  const { data: users, error: usersError } = useUsers({ limit: 1000000 }) // 一回で全て取得するために適当に大きな数字
  const [customerNames, setCustomerNames] = useState<TOptions[] | undefined>(undefined)

  useEffect(() => {
    handleFetchFile().then()
  }, [])

  useEffect(() => {
    if (usersError) {
      handleOpenWithErrorMessage(usersError)
    }
  }, [usersError])

  useEffect(() => {
    if (users) {
      setCustomerNames(
        users?.items.map((user: any) => {
          return {
            userId: user.id,
            name: user.name ?? '',
            email: user.email ?? '',
          }
        })
      )
    }
  }, [users])

  /**
   * handleFetchFile
   */
  const handleFetchFile = async (): Promise<void> => {
    try {
      const data = await orderRepository.getFile()
      setListFile(data)
      /* eslint-disable  @typescript-eslint/no-explicit-any */
    } catch (e: any) {
      setIsError(true)
      handleOpenWithErrorMessage(e.message)
    }
  }

  /**
   * handleSubmit
   * @param e
   */
  const handleSubmit = async (e: React.ChangeEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    if (!selectedUserIdName) {
      return handleOpenWithErrorMessage('select user')
    }
    if ((!user && !file) || file?.name === '') {
      return handleOpenWithErrorMessage('select upload file')
    }
    setIsLoading(true)
    try {
      const payload: IPostStatementRequest = {
        userId: id ? Number(user?.userId) : Number(selectedUserIdName),
        file: {
          // @ts-ignore
          filename: file?.name ?? user?.file.filename,
          path: listFile?.path ?? '',
        },
      }

      if (!id) {
        await orderRepository.postStatement(payload)
      } else {
        await orderRepository.patchStatement(Number(id), payload)
      }
      handleOpenWithSuccessMessage('Success!!')
      /* eslint-disable  @typescript-eslint/no-explicit-any */
    } catch (e: any) {
      handleOpenWithErrorMessage(e.message)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * handleFileChange
   * @param file
   */
  const handleFileChange = async (file: File): Promise<void> => {
    try {
      setFile(file)
      if (listFile) {
        setIsLoading(true)
        await orderRepository.putFileUpload(listFile.url, file)
      }
      setIsDeleteFile(false)
      /* eslint-disable  @typescript-eslint/no-explicit-any */
    } catch (e: any) {
      handleOpenWithErrorMessage(e.message)
      setIsError(true)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * handleDeleteFile
   */
  const handleDeleteFile = () => {
    // @ts-ignore
    setFile()
    setIsDeleteFile(true)
  }

  return (
    <form onSubmit={handleSubmit}>
      <BackButton location={location} />
      <CardWithHeaderAndActionButton
        headerTitle={t('statement_account_detail.title')}
        buttonSize="small"
        buttonText={t('statement_account_detail.save')}
        loading={isLoading}
        disabled={isDelete || !file}
      >
        <OrderStatementOfAccountContent label={t('statement_account_detail.customer_name')}>
          <Autocomplete
            // @ts-ignore
            defaultValue={{ name: selectedUserName }}
            disabled={!!user?.name}
            autoHighlight
            autoComplete
            size={'small'}
            sx={{ width: 220, backgroundColor: '#FFFFFF' }}
            freeSolo
            onChange={(e, value) => {
              // @ts-ignore
              setSelectedUserIdName(value.userId)
              // @ts-ignore
              setSelectedName(value.name)
            }}
            options={uniqBy(customerNames, 'name')}
            getOptionLabel={(option: any) => option?.name}
            // @ts-ignore
            renderOption={(props, option: TOptions) => (
              <Box component="li" sx={BoxStyle} {...props}>
                <Typography sx={TextStyle}>{option?.name}</Typography>
                <Typography sx={TextStyle2} paddingLeft={'4px'}>
                  {option?.email}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField placeholder={'XXXXXXX'} sx={TextStyle3} label={''} {...params} />
            )}
          />
        </OrderStatementOfAccountContent>
        <OrderStatementOfAccountContent
          label={t('statement_account_detail.file_upload')}
          sx={{ marginTop: '8px' }}
          alignItems={user?.file.url !== '' ? 'baseline' : 'start'}
        >
          <Box sx={ColumnBoxStyle} mt={1}>
            <FileUploader
              onSaveFile={handleFileChange}
              onDeleteFile={handleDeleteFile}
              initFile={user?.file}
              isLoading={isLoading}
              isError={isError}
              fileText={t('statement_account_detail.drop_your_files_here_or')}
              uploadText={t('statement_account_detail.upload')}
            />
          </Box>
        </OrderStatementOfAccountContent>
      </CardWithHeaderAndActionButton>
    </form>
  )
}
